import React from "react";

interface PaymentReceiptProps {
  payment: {
    id: number;
    amount: number;
    currency: string;
    status: string;
    paymentType: string;
    createdAt: string;
    transactions: { paymentTransId: string }[];
  };
  guarantorNumber?: string;
  guarantorLastName?: string;
  onClose: () => void;
}

/**
 * Patient-facing payment receipt — mirrors the staff portal's receipt
 * (provider-dashboard PaymentReceipt) so both sides print an identical document.
 * The modal is a preview; "Print / Save PDF" opens a print-ready window.
 */
const PaymentReceipt: React.FC<PaymentReceiptProps> = ({
  payment,
  guarantorNumber,
  guarantorLastName,
  onClose,
}) => {
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString('en-US');
    } catch {
      return dateString;
    }
  };

  const tx = payment.transactions?.[0];
  const statusLabel = (payment.status || "").toUpperCase();
  const paymentTypeLabel = (payment.paymentType || "ONE_TIME").toUpperCase();

  // Escape any field-derived value interpolated into the print HTML. It's the patient's own data in
  // their own window, but this receipt gets copied around and string-built HTML shouldn't trust content.
  const escapeHtml = (v: unknown) =>
    String(v ?? "").replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));

  const handlePrint = () => {
    const printWindow = window.open("", "_blank", "width=600,height=800");
    if (!printWindow) return;

    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>Payment Receipt #${payment.id}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; color: #1e293b; background: #fff; padding: 40px; }
    .receipt { max-width: 480px; margin: 0 auto; }
    .header { text-align: center; padding-bottom: 20px; margin-bottom: 20px; border-bottom: 2px solid #6b1c2a; }
    .header h1 { font-size: 22px; color: #6b1c2a; font-weight: 700; margin-bottom: 2px; }
    .header .sub { font-size: 11px; color: #64748b; letter-spacing: 0.5px; }
    .title { text-align: center; font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 2px; color: #475569; margin: 20px 0 16px; }
    .section { margin-bottom: 16px; }
    .section-label { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: #94a3b8; margin-bottom: 8px; }
    .row { display: flex; justify-content: space-between; align-items: center; padding: 5px 0; font-size: 13px; }
    .row .label { color: #64748b; }
    .row .value { font-weight: 600; color: #1e293b; text-align: right; }
    .row .value.mono { font-family: 'Courier New', monospace; font-size: 11px; }
    .divider { border: none; border-top: 1px solid #e2e8f0; margin: 14px 0; }
    .total-row { display: flex; justify-content: space-between; align-items: center; padding: 14px 0; margin-top: 12px; border-top: 2px solid #6b1c2a; }
    .total-row .label { font-size: 15px; font-weight: 600; color: #334155; }
    .total-row .value { font-size: 22px; font-weight: 700; color: #6b1c2a; }
    .footer { text-align: center; margin-top: 28px; padding-top: 16px; border-top: 1px solid #e2e8f0; }
    .footer p { font-size: 11px; color: #94a3b8; line-height: 1.6; }
    .badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; }
    .badge-success { background: #dcfce7; color: #166534; }
    .badge-pending { background: #fef9c3; color: #854d0e; }
    .badge-failed { background: #fee2e2; color: #991b1b; }
    .badge-default { background: #f1f5f9; color: #475569; }
    .timestamp { font-size: 10px; color: #94a3b8; text-align: center; margin-top: 12px; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <div class="receipt">
    <div class="header">
      <h1>St. Luke Hospital</h1>
      <div class="sub">Payment processed by Stitchfin</div>
    </div>

    <div class="title">Payment Receipt</div>

    <div class="section">
      <div class="section-label">Payment Details</div>
      <div class="row"><span class="label">Receipt #</span><span class="value">${payment.id}</span></div>
      <div class="row"><span class="label">Date</span><span class="value">${formatDate(payment.createdAt)}</span></div>
      <div class="row"><span class="label">Status</span><span class="value"><span class="badge ${statusLabel.includes("SUCCESS") || statusLabel.includes("COMPLETED") ? "badge-success" : statusLabel.includes("FAIL") ? "badge-failed" : statusLabel.includes("PEND") ? "badge-pending" : "badge-default"}">${escapeHtml(statusLabel)}</span></span></div>
    </div>

    <hr class="divider" />

    <div class="section">
      <div class="section-label">Patient Information</div>
      ${guarantorLastName ? `<div class="row"><span class="label">Guarantor</span><span class="value">${escapeHtml(guarantorLastName)}</span></div>` : ""}
      ${guarantorNumber ? `<div class="row"><span class="label">Guarantor #</span><span class="value">${escapeHtml(guarantorNumber)}</span></div>` : ""}
      <div class="row"><span class="label">Payment Type</span><span class="value">${escapeHtml(paymentTypeLabel)}</span></div>
      ${tx?.paymentTransId ? `<div class="row"><span class="label">Transaction ID</span><span class="value mono">${escapeHtml(tx.paymentTransId)}</span></div>` : ""}
    </div>

    <div class="total-row">
      <span class="label">Total Paid</span>
      <span class="value">${formatCurrency(payment.amount)}</span>
    </div>

    <div class="footer">
      <p>Thank you for your payment.</p>
      <p>For questions, please contact the billing department.</p>
    </div>

    <div class="timestamp">Printed on ${new Date().toLocaleString('en-US')}</div>
  </div>
  <script>window.onload = function() { window.print(); }</script>
</body>
</html>`);
    printWindow.document.close();
  };

  const statusColor =
    statusLabel.includes("SUCCESS") || statusLabel.includes("COMPLETED")
      ? "bg-green-100 text-green-800"
      : statusLabel.includes("FAIL")
        ? "bg-red-100 text-red-800"
        : statusLabel.includes("PEND")
          ? "bg-yellow-100 text-yellow-800"
          : "bg-gray-100 text-gray-800";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal receipt preview */}
        <div className="p-8">
          {/* Header */}
          <div className="flex flex-col items-center pb-5 mb-5 border-b-2 border-[#6b1c2a]">
            <h1 className="text-xl font-bold text-[#6b1c2a]">St. Luke Hospital</h1>
            <p className="text-xs text-slate-400 tracking-wide mt-1">Payment processed by Stitchfin</p>
          </div>

          {/* Title */}
          <h3 className="text-center text-xs font-semibold uppercase tracking-[2px] text-slate-500 mb-5">
            Payment Receipt
          </h3>

          {/* Payment Details */}
          <div className="mb-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
              Payment Details
            </p>
            <div className="space-y-1.5">
              <Row label="Receipt #" value={String(payment.id)} />
              <Row label="Date" value={formatDate(payment.createdAt)} />
              <div className="flex justify-between items-center py-1 text-sm">
                <span className="text-slate-500">Status</span>
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${statusColor}`}>
                  {statusLabel}
                </span>
              </div>
            </div>
          </div>

          <hr className="border-slate-200 my-4" />

          {/* Patient Information */}
          <div className="mb-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
              Patient Information
            </p>
            <div className="space-y-1.5">
              {guarantorLastName && <Row label="Guarantor" value={guarantorLastName} />}
              {guarantorNumber && <Row label="Guarantor #" value={guarantorNumber} />}
              <Row label="Payment Type" value={paymentTypeLabel} />
              {tx?.paymentTransId && (
                <div className="flex justify-between items-center py-1 text-sm">
                  <span className="text-slate-500">Transaction ID</span>
                  <span className="font-semibold text-slate-900 font-mono text-xs">
                    {tx.paymentTransId}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Total */}
          <div className="flex justify-between items-center pt-4 mt-3 border-t-2 border-[#6b1c2a]">
            <span className="text-base font-semibold text-slate-700">Total Paid</span>
            <span className="text-2xl font-bold text-[#6b1c2a]">{formatCurrency(payment.amount)}</span>
          </div>

          {/* Footer */}
          <div className="text-center mt-6 pt-4 border-t border-slate-200">
            <p className="text-xs text-slate-400">Thank you for your payment.</p>
            <p className="text-xs text-slate-400 mt-0.5">
              For questions, please contact the billing department.
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-3 px-8 pb-6 pt-2">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
          >
            Close
          </button>
          <button
            onClick={handlePrint}
            className="px-5 py-2 text-sm font-medium bg-[#6b1c2a] text-white rounded-lg hover:bg-[#5a1723] transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print / Save PDF
          </button>
        </div>
      </div>
    </div>
  );
};

/** Reusable row for the modal preview */
const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between items-center py-1 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-semibold text-slate-900">{value}</span>
  </div>
);

export default PaymentReceipt;
